import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Toggle from './ui/Toggle';
import { UserService } from '../api/generated/services/UserService';
import type { NotificationPreferencesDataView } from '../api/generated/models/NotificationPreferencesDataView';
import type { NotificationPreferencesRequest } from '../api/generated/models/NotificationPreferencesRequest';
import { addNotification } from '../store/slices/notificationSlice';
import type { AppDispatch } from '../store/store';
import { ApiError } from '../api/generated/core/ApiError';
import { helper } from '../utils/helper';

const NotificationsTab: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState<NotificationPreferencesDataView | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await UserService.getAppUserNotificationPreferencesGet();
        if (!cancelled) {
          setPrefs(response.preferences ?? {});
        }
      } catch (err) {
        if (!cancelled) {
          let message = t('notification_settings.load_failed');
          if (err instanceof ApiError) {
            const parsed = helper.parseApiErrorBody(err);
            if (parsed?.error) {
              message = parsed.error;
            }
          }
          dispatch(addNotification({
            type: 'error',
            title: t('notification_error'),
            message,
          }));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [dispatch, t]);

  const updatePref = (key: keyof NotificationPreferencesRequest, value: boolean) => {
    setPrefs((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async () => {
    if (!prefs) {
      return;
    }
    setSaving(true);
    try {
      const body: NotificationPreferencesRequest = {
        task_assigned_push: prefs.task_assigned_push === true,
        task_assigned_email: prefs.task_assigned_email === true,
        task_changed_push: prefs.task_changed_push === true,
        task_changed_email: prefs.task_changed_email === true,
        task_email_bundling: prefs.task_email_bundling === true,
      };
      const response = await UserService.putAppUserNotificationPreferencesPut(body);
      setPrefs(response.preferences ?? body);
      dispatch(addNotification({
        type: 'success',
        title: t('notification_success'),
        message: t('notification_settings.saved'),
      }));
    } catch (err) {
      let message = t('notification_settings.save_failed');
      if (err instanceof ApiError) {
        const parsed = helper.parseApiErrorBody(err);
        if (parsed?.error) {
          message = parsed.error;
        }
      }
      dispatch(addNotification({
        type: 'error',
        title: t('notification_error'),
        message,
      }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="notification-settings-tab notification-settings-tab--loading">
        <div className="loading-spinner" aria-label={t('loading_text')} />
      </div>
    );
  }

  if (!prefs) {
    return null;
  }

  return (
    <div className="notification-settings-tab">
      <h2 className="notification-settings-tab__title">{t('notification_settings.title')}</h2>
      <p className="notification-settings-tab__intro">{t('notification_settings.intro')}</p>

      <div className="notification-settings-card">
        <div className="notification-settings-card__header">
          <span className="notification-settings-card__task-col" />
          <span className="notification-settings-card__col">{t('notification_settings.push_column')}</span>
          <span className="notification-settings-card__col">{t('notification_settings.email_column')}</span>
        </div>

        <div className="notification-settings-row">
          <div className="notification-settings-row__info">
            <span className="notification-settings-row__label">
              {t('notification_settings.task_assigned')}
            </span>
            <span className="notification-settings-row__hint">
              {t('notification_settings.task_assigned_hint')}
            </span>
          </div>
          <div className="notification-settings-row__cell">
            <Toggle
              checked={prefs.task_assigned_push === true}
              onChange={(value) => updatePref('task_assigned_push', value)}
              disabled={saving}
            />
          </div>
          <div className="notification-settings-row__cell">
            <Toggle
              checked={prefs.task_assigned_email === true}
              onChange={(value) => updatePref('task_assigned_email', value)}
              disabled={saving}
            />
          </div>
        </div>

        <div className="notification-settings-row">
          <div className="notification-settings-row__info">
            <span className="notification-settings-row__label">
              {t('notification_settings.task_changed')}
            </span>
            <span className="notification-settings-row__hint">
              {t('notification_settings.task_changed_hint')}
            </span>
          </div>
          <div className="notification-settings-row__cell">
            <Toggle
              checked={prefs.task_changed_push === true}
              onChange={(value) => updatePref('task_changed_push', value)}
              disabled={saving}
            />
          </div>
          <div className="notification-settings-row__cell">
            <Toggle
              checked={prefs.task_changed_email === true}
              onChange={(value) => updatePref('task_changed_email', value)}
              disabled={saving}
            />
          </div>
        </div>

        <div className="notification-settings-row">
          <div className="notification-settings-row__info">
            <span className="notification-settings-row__label">
              {t('notification_settings.task_bundle')}
            </span>
            <span className="notification-settings-row__hint">
              {t('notification_settings.task_bundle_hint')}
            </span>
          </div>
          <div className="notification-settings-row__cell notification-settings-row__cell--dash">
            —
          </div>
          <div className="notification-settings-row__cell">
            <Toggle
              checked={prefs.task_email_bundling === true}
              onChange={(value) => updatePref('task_email_bundling', value)}
              disabled={saving}
            />
          </div>
        </div>
      </div>

      <p className="notification-settings-tab__note">{t('notification_settings.own_actions_note')}</p>
      <p className="notification-settings-tab__hint">{t('notification_settings.push_mobile_hint')}</p>

      <div className="notification-settings-tab__actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {t('saveText')}
        </button>
      </div>
    </div>
  );
};

export default NotificationsTab;
